// src/services/printer/printer.service.js
import { ESCPOSCommands } from "./commands";
import { PrinterStorage } from "./printerStorage";
import {
  printerConfigService,
  PRINT_DESTINATIONS,
} from "./printerConfigService";

class MultiPrinterService {
  constructor() {
    this.printerHandlers = new Map();
    this.encoder = new TextEncoder();
    this.MAX_RECONNECT_ATTEMPTS = 3;
    this.loadSavedConfigurations();
  }

  loadSavedConfigurations() {
    try {
      // Asegurarnos que printerHandlers esté inicializado
      if (!this.printerHandlers) {
        this.printerHandlers = new Map();
      }

      const savedConfigs =
        JSON.parse(localStorage.getItem("printerConfigurations")) || {};
      const connectedPrinters =
        JSON.parse(localStorage.getItem("connectedPrinters")) || [];

      connectedPrinters.forEach((printer) => {
        const defaultConfig = {
          printKitchenOrders: false,
          printBarOrders: false,
          autoCustomerBills: false,
        };

        const printerConfig = {
          ...defaultConfig,
          ...(savedConfigs[printer.id] || {}),
        };

        this.printerHandlers.set(printer.id, {
          device: null,
          characteristic: null,
          server: null,
          name: printer.name,
          config: printerConfig,
        });
      });
    } catch (error) {
      console.error("Error loading printer configurations:", error);
      // Si hay un error, asegurarnos de tener un Map vacío
      this.printerHandlers = new Map();
    }
  }

  initializePrinters() {
    const { configs, printers } = PrinterStorage.load();

    printers.forEach((printer) => {
      this.printerHandlers.set(printer.id, {
        device: null,
        characteristic: null,
        server: null,
        name: printer.name,
        config: configs[printer.id] || {
          printKitchenOrders: false,
          printBarOrders: false,
          autoCustomerBills: false,
        },
      });
    });
  }

  // async connectPrinter() {
  //   try {
  //     const device = await navigator.bluetooth.requestDevice({
  //       acceptAllDevices: true,
  //       optionalServices: [
  //         "000018f0-0000-1000-8000-00805f9b34fb",
  //         "49535343-fe7d-4ae5-8fa9-9fafd205e455",
  //         "e7810a71-73ae-499d-8c15-faa9aef0c3f2",
  //         "0000fff0-0000-1000-8000-00805f9b34fb",
  //       ],
  //     });

  //     const server = await device.gatt.connect();
  //     const service = await this.getService(server);
  //     const characteristic = await this.getWriteCharacteristic(service);

  //     if (!characteristic) {
  //       throw new Error("No se encontró característica de escritura");
  //     }

  //     this.setupPrinterHandler(device, server, characteristic);
  //     return device.id;
  //   } catch (error) {
  //     throw new Error(`Error de conexión: ${error.message}`);
  //   }
  // }
  async connectPrinter() {
    try {
      // Asegurarnos que printerHandlers esté inicializado
      if (!this.printerHandlers) {
        this.printerHandlers = new Map();
      }

      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: [
          "000018f0-0000-1000-8000-00805f9b34fb",
          "49535343-fe7d-4ae5-8fa9-9fafd205e455",
          "e7810a71-73ae-499d-8c15-faa9aef0c3f2",
          "0000fff0-0000-1000-8000-00805f9b34fb",
          "00001101-0000-1000-8000-00805f9b34fb", // Serial Port
          "00001200-0000-1000-8000-00805f9b34fb", // PnP Information
        ],
      });

      console.log("Dispositivo seleccionado:", device);

      const server = await device.gatt.connect();
      console.log("Conectado a ", server);
      const service = await this.getService(server);
      console.log("Servicio recuperado ", service);
      const characteristic = await this.getWriteCharacteristic(service);
      console.log("Caracteristicas recuperadas ", characteristic);

      if (!characteristic) {
        throw new Error("No se encontró característica de escritura");
      }

      // Crear el handler para la nueva impresora
      const printerHandler = {
        device,
        server,
        characteristic,
        name: device.name || "Impresora sin nombre",
        config: {
          printKitchenOrders: false,
          printBarOrders: false,
          autoCustomerBills: false,
        },
      };
      console.log("PrinterHandler ", printerHandler);

      // Guardar el handler
      this.printerHandlers.set(device.id, printerHandler);

      // Guardar la configuración
      this.savePrinterConfigurations();

      // Configurar el listener de desconexión
      this.setupDisconnectionListener(device);

      return device.id;
    } catch (error) {
      console.error("Error en la conexión:", error);
      throw new Error(`Error de conexión: ${error.message}`);
    }
  }

  async testPrint(printerId) {
    try {
      const handler = await this.ensureConnection(printerId);

      await this.sendCommand(handler.characteristic, ESCPOSCommands.INIT);
      await this.sendCommand(handler.characteristic, ESCPOSCommands.CENTER);
      await this.sendCommand(handler.characteristic, ESCPOSCommands.BOLD_ON);
      await this.writeText(handler.characteristic, "PRUEBA DE IMPRESIÓN\n\n");
      await this.sendCommand(handler.characteristic, ESCPOSCommands.BOLD_OFF);
      await this.writeText(
        handler.characteristic,
        "Impresora configurada correctamente\n"
      );
      await this.writeText(
        handler.characteristic,
        `${new Date().toLocaleString()}\n\n\n`
      );
      await this.sendCommand(handler.characteristic, ESCPOSCommands.CUT);

      return true;
    } catch (error) {
      console.error("Error en prueba de impresión:", error);
      throw error;
    }
  }

  async printBarOrder(order, printerId) {
    try {
      const handler = await this.ensureConnection(printerId);

      await this.sendCommand(handler.characteristic, ESCPOSCommands.INIT);
      await this.sendCommand(handler.characteristic, ESCPOSCommands.CENTER);
      await this.sendCommand(handler.characteristic, ESCPOSCommands.BOLD_ON);
      await this.writeText(handler.characteristic, "COMANDA BAR\n\n");
      await this.sendCommand(handler.characteristic, ESCPOSCommands.BOLD_OFF);
      await this.sendCommand(handler.characteristic, ESCPOSCommands.LEFT);

      await this.writeText(
        handler.characteristic,
        `Mesa: ${order.descriptionTable}\n`
      );
      await this.writeText(
        handler.characteristic,
        `Fecha: ${new Date().toLocaleString()}\n`
      );
      await this.writeText(
        handler.characteristic,
        "------------------------\n"
      );

      for (const item of order.items) {
        await this.writeText(
          handler.characteristic,
          `${item.quantity}x ${item.name}\n`
        );
        if (item.selectedAddon) {
          await this.writeText(
            handler.characteristic,
            `  Opción: ${item.selectedAddon}\n`
          );
        }
        if (item.comment) {
          await this.writeText(
            handler.characteristic,
            `  Nota: ${item.comment}\n`
          );
        }
      }

      await this.writeText(handler.characteristic, "\n\n\n");
      await this.sendCommand(handler.characteristic, ESCPOSCommands.CUT);
    } catch (error) {
      console.error("Error imprimiendo comanda bar:", error);
      throw error;
    }
  }

  async printOrder(orderData, destination, items) {
    try {
      // Obtener todas las impresoras configuradas
      const printers = Array.from(this.printerHandlers.entries());
      console.log("Impresoras ", printers);
      // Filtrar las impresoras disponibles según el destino
      const availablePrinters = printers.filter(([id, printer]) => {
        if (destination === "COMANDA COCINA") {
          return printer.config.printKitchenOrders;
        } else if (destination === "COMANDA BAR") {
          // La impresora de barra maneja tanto "Barra" como "Bebidas"
          return printer.config.printBarOrders;
        }
        return false;
      });

      console.log(`Buscando impresoras para ${destination}`, availablePrinters);

      // if (availablePrinters.length === 0) {
      //   throw new Error(`No hay impresoras configuradas para ${destination}`);
      // }

      // Tomar la primera impresora disponible
      const [printerId, printer] = availablePrinters[0];

      if (!printerId) {
        throw new Error(
          `No se pudo obtener el ID de la impresora para ${destination}`
        );
      }

      console.log(`Imprimiendo en impresora ${printerId} para ${destination}`);

      const handler = await this.ensureConnection(printerId);

      // Imprimir el encabezado
      await this.sendCommand(handler.characteristic, ESCPOSCommands.INIT);
      await this.sendCommand(handler.characteristic, ESCPOSCommands.CENTER);
      await this.sendCommand(handler.characteristic, ESCPOSCommands.BOLD_ON);
      await this.writeText(handler.characteristic, `${destination}\n\n`);
      await this.sendCommand(handler.characteristic, ESCPOSCommands.BOLD_OFF);
      await this.sendCommand(handler.characteristic, ESCPOSCommands.LEFT);

      // Información de la mesa y fecha
      await this.writeText(
        handler.characteristic,
        `Mesa: ${orderData.descriptionTable}\n`
      );
      await this.writeText(
        handler.characteristic,
        `Fecha: ${new Date().toLocaleString()}\n`
      );
      await this.writeText(
        handler.characteristic,
        "------------------------\n"
      );

      // Imprimir cada ítem
      for (const item of items) {
        console.log("IMPRIME ", item);
        await this.writeText(
          handler.characteristic,
          `${item.quantity}x ${item.name}\n`
        );
        if (item.selectedAddon) {
          await this.writeText(
            handler.characteristic,
            `  Opción: ${item.selectedAddon}\n`
          );
        }
        if (item.comment) {
          await this.writeText(
            handler.characteristic,
            `  Nota: ${item.comment}\n`
          );
        }
      }

      // Pie de página
      await this.writeText(handler.characteristic, "\n\n\n");
      await this.sendCommand(handler.characteristic, ESCPOSCommands.CUT);

      console.log(`Impresión completada para ${destination}`);
      return true;
    } catch (error) {
      console.error(`Error imprimiendo ${destination}:`, error);
      throw error;
    }
  }

  async printCustomerBill(order, printerId) {
    try {
      console.log("Imprimiendo ticket de cliente para orden:", order);

      const handler = await this.ensureConnection(printerId);
      if (!handler || !handler.characteristic) {
        throw new Error("Impresora no disponible");
      }

      // Validar que la orden tenga los datos necesarios
      if (!order || !order.items || !Array.isArray(order.items)) {
        console.error("Datos de orden inválidos:", order);
        throw new Error("Datos de orden inválidos para impresión");
      }

      await this.sendCommand(handler.characteristic, ESCPOSCommands.INIT);
      await this.sendCommand(handler.characteristic, ESCPOSCommands.CENTER);
      await this.sendCommand(handler.characteristic, ESCPOSCommands.BOLD_ON);
      await this.writeText(handler.characteristic, "TICKET DE PAGO\n\n");
      await this.sendCommand(handler.characteristic, ESCPOSCommands.BOLD_OFF);
      await this.sendCommand(handler.characteristic, ESCPOSCommands.LEFT);

      // Información del ticket
      await this.writeText(
        handler.characteristic,
        `Ticket #${order._id || order.id}\n`
      );
      await this.writeText(
        handler.characteristic,
        `Mesa: ${order.descriptionTable}\n`
      );
      await this.writeText(
        handler.characteristic,
        `Fecha: ${new Date().toLocaleString()}\n`
      );
      await this.writeText(
        handler.characteristic,
        "------------------------\n"
      );

      let total = 0;

      // Imprimir items
      for (const item of order.items) {
        const itemTotal = item.price * item.quantity;
        total += itemTotal;

        await this.writeText(
          handler.characteristic,
          `${item.quantity}x ${item.name}\n` +
            `  ${item.price.toFixed(2)} c/u = ${itemTotal.toFixed(2)}\n`
        );
        if (item.selectedAddon) {
          await this.writeText(
            handler.characteristic,
            `  Opción: ${item.selectedAddon}\n`
          );
        }
      }

      await this.writeText(
        handler.characteristic,
        "------------------------\n"
      );
      await this.sendCommand(handler.characteristic, ESCPOSCommands.BOLD_ON);
      await this.writeText(
        handler.characteristic,
        `TOTAL: $${total.toFixed(2)}\n`
      );
      await this.sendCommand(handler.characteristic, ESCPOSCommands.BOLD_OFF);
      await this.writeText(
        handler.characteristic,
        `Forma de pago: ${order.payment || "No especificado"}\n\n\n`
      );
      await this.writeText(handler.characteristic, "Gracias por Elegirnos!\n");

      await this.sendCommand(handler.characteristic, ESCPOSCommands.CUT);

      console.log("Ticket de cliente impreso exitosamente");
      return true;
    } catch (error) {
      console.error("Error imprimiendo ticket de cliente:", error);
     // throw error;
    }
  }

  async printKitchenOrder(order, printerId) {
    try {
      const handler = await this.ensureConnection(printerId);

      await this.sendCommand(handler.characteristic, ESCPOSCommands.INIT);
      await this.sendCommand(handler.characteristic, ESCPOSCommands.CENTER);
      await this.sendCommand(handler.characteristic, ESCPOSCommands.BOLD_ON);
      await this.writeText(handler.characteristic, "COMANDA COCINA\n\n");
      await this.sendCommand(handler.characteristic, ESCPOSCommands.BOLD_OFF);
      await this.sendCommand(handler.characteristic, ESCPOSCommands.LEFT);

      await this.writeText(
        handler.characteristic,
        `Mesa: ${order.descriptionTable}\n`
      );
      await this.writeText(
        handler.characteristic,
        `Fecha: ${new Date().toLocaleString()}\n`
      );
      await this.writeText(
        handler.characteristic,
        "------------------------\n"
      );

      for (const item of order.items) {
        await this.writeText(
          handler.characteristic,
          `${item.quantity}x ${item.name}\n`
        );
        if (item.comment) {
          await this.writeText(
            handler.characteristic,
            `  Nota: ${item.comment}\n`
          );
        }
      }

      await this.writeText(handler.characteristic, "\n\n\n");
      await this.sendCommand(handler.characteristic, ESCPOSCommands.CUT);
    } catch (error) {
      console.error("Error imprimiendo comanda cocina:", error);
      throw error;
    }
  }

  async printProductSummary(printerId, sales, metrics) {
    // Verificar que el printerId existe
    if (!printerId) {
      throw new Error("ID de impresora no proporcionado");
    }

    // Verificar que la impresora existe en el Map
    const handler = this.printerHandlers.get(printerId);
    if (!handler) {
      throw new Error("Impresora no encontrada");
    }

    // Asegurar la conexión antes de imprimir
    await this.ensureConnection(printerId);

    // Volver a obtener el handler después de asegurar la conexión
    const updatedHandler = this.printerHandlers.get(printerId);
    if (!updatedHandler || !updatedHandler.characteristic) {
      throw new Error("No se pudo establecer la conexión con la impresora");
    }

    try {
      // 1. Procesar y consolidar los productos de todas las órdenes
      const consolidatedProducts = consolidateProducts(sales);

      // 2. Formatear el contenido del ticket
      const ticketContent = formatTicketContent(consolidatedProducts);

      // 3. Enviar a la impresora
      // await sendToPrinter(printerId, ticketContent);
      const encoder = new TextEncoder();
      const data = encoder.encode(ticketContent);
      const { characteristic } = handler;

      // Aquí iría la lógica específica de tu impresora
      // await this.writeText(printerId, data);
      await this.sendCommand(characteristic, ESCPOSCommands.INIT);
      await this.sendCommand(characteristic, ESCPOSCommands.CENTER);
      await this.sendCommand(characteristic, ESCPOSCommands.BOLD_ON);
      await this.writeText(characteristic, "ARQUEO DEL DÍA\n\n");
      await this.sendCommand(characteristic, ESCPOSCommands.BOLD_OFF);
      await this.sendCommand(characteristic, ESCPOSCommands.LEFT);

      // Fecha y hora
      const now = new Date();
      await this.writeText(
        characteristic,
        `Fecha: ${now.toLocaleDateString()}\n`
      );
      await this.writeText(
        characteristic,
        `Hora: ${now.toLocaleTimeString()}\n`
      );
      await this.writeText(characteristic, "------------------------\n\n");

      // Detalles de ventas
      // await this.writeText(characteristic, "RESUMEN DE VENTAS\n");

      console.log("Ticket Content ", ticketContent);
      await this.writeText(characteristic, ticketContent);

      // Pie de página
      await this.writeText(characteristic, "\n------------------------\n");
      await this.writeText(characteristic, "Fin del reporte\n\n\n");

      return true;
    } catch (error) {
      console.error("Error al imprimir resumen:", error);
      throw new Error("No se pudo imprimir el resumen de productos");
    }

    function consolidateProducts(orders) {
      // Crear un mapa para consolidar productos
      const productMap = new Map();

      // Procesar cada orden y sus items
      console.log(orders);
      orders.transactions.forEach((order) => {
        order.items.forEach((item) => {
          const existingProduct = productMap.get(item.name);

          if (existingProduct) {
            // Si el producto ya existe, actualizar cantidad y total
            existingProduct.quantity += item.quantity;
            existingProduct.total += item.quantity * item.price;
          } else {
            // Si es nuevo, agregar al mapa
            productMap.set(item.name, {
              name: item.name,
              quantity: item.quantity,
              price: item.price,
              total: item.quantity * item.price,
            });
          }
        });
      });

      // Convertir el mapa a array
      return Array.from(productMap.values());
    }

    function formatTicketContent(products) {
      // Comandos ESC/POS para formato
      const ESC = "\x1B";
      const CENTER = `${ESC}\x61\x01`;
      const LEFT = `${ESC}\x61\x00`;

      // Calcular el total general
      const totalAmount = products.reduce(
        (sum, product) => sum + product.total,
        0
      );

      // Construir el encabezado
      let content =
        CENTER +
        "RESUMEN DE PRODUCTOS\n" +
        LEFT +
        "--------------------------------\n\n";

      // Agregar cada línea de producto
      products.forEach((product) => {
        // Formatear cada columna con padding adecuado
        const name = product.name.padEnd(20);
        const quantity = product.quantity.toString().padStart(10);
        const unitPrice = `$${product.price.toFixed(2)}`.padStart(15);
        const total = `$${product.total.toFixed(2)}`.padStart(15);

        content += `${name}${quantity}${unitPrice}${total}\n`;
      });

      // Agregar el total
      content +=
        "\n--------------------------------\n" +
        `Total del Día:`.padStart(45) +
        `$${totalAmount.toFixed(2)}`.padStart(15) +
        "\n\n";

      // Agregar comando de corte
      content += "\x1D\x56\x41\n";

      return content;
    }
  }

  async printCancellationTicket(orderId, cancelledProduct, printerId) {
    console.log("[servicio printCancellationTicket]");
    try {
      if (!printerId) {
        throw new Error("ID de impresora no proporcionado");
      }

      const handler = this.printerHandlers.get(printerId);
      if (!handler) {
        throw new Error("Impresora no encontrada");
      }

      await this.ensureConnection(printerId);

      const { characteristic } = handler;
      await this.sendCommand(characteristic, ESCPOSCommands.INIT);
      await this.sendCommand(characteristic, ESCPOSCommands.CENTER);
      await this.sendCommand(characteristic, ESCPOSCommands.BOLD_ON);
      await this.writeText(characteristic, "TICKET DE CANCELACIÓN\n\n");
      await this.sendCommand(characteristic, ESCPOSCommands.BOLD_OFF);
      await this.sendCommand(characteristic, ESCPOSCommands.LEFT);

      await this.writeText(characteristic, `Ticket #${orderId}\n`);
      await this.writeText(characteristic, `Mesa: ${cancelledProduct.table}\n`);
      await this.writeText(
        characteristic,
        `Fecha: ${new Date().toLocaleString()}\n`
      );
      await this.writeText(characteristic, "------------------------\n");

      // Detalles del producto cancelado
      await this.writeText(characteristic, "PRODUCTO CANCELADO:\n");
      await this.writeText(characteristic, `${cancelledProduct.name}\n`);
      if (cancelledProduct.quantity > 1) {
        await this.writeText(
          characteristic,
          `Cantidad: ${cancelledProduct.quantity}\n`
        );
      }

      await this.writeText(characteristic, "------------------------\n");
      await this.sendCommand(characteristic, ESCPOSCommands.CENTER);
      await this.writeText(characteristic, "CANCELACIÓN CONFIRMADA\n\n\n");
      await this.sendCommand(characteristic, ESCPOSCommands.CUT);

      return true;
    } catch (error) {
      console.error("Error imprimiendo ticket de cancelación:", error);
      throw error;
    }
  }

  async printDailyReport(printerId, sales) {
    // Verificar que el printerId existe
    if (!printerId) {
      throw new Error("ID de impresora no proporcionado");
    }

    // Verificar que la impresora existe en el Map
    const handler = this.printerHandlers.get(printerId);
    if (!handler) {
      throw new Error("Impresora no encontrada");
    }

    // Asegurar la conexión antes de imprimir
    await this.ensureConnection(printerId);

    // Volver a obtener el handler después de asegurar la conexión
    const updatedHandler = this.printerHandlers.get(printerId);
    if (!updatedHandler || !updatedHandler.characteristic) {
      throw new Error("No se pudo establecer la conexión con la impresora");
    }
    const { characteristic } = handler;
    const transactions = sales.data.salesByDate.transactions; // Acceder a transactions dentro del objeto sales

    // Calcular totales
    const totals = {
      cash: transactions
        .filter((s) => s.payment === "Efectivo")
        .reduce((acc, s) => acc + s.amount, 0),
      card: transactions
        .filter((s) => s.payment === "Tarjeta")
        .reduce((acc, s) => acc + s.amount, 0),
    };

    const total = totals.cash + totals.card;
    const orderCount = transactions.length;
    const avgTicket = orderCount > 0 ? total / orderCount : 0;

    // Iniciar impresión
    await this.sendCommand(characteristic, ESCPOSCommands.INIT);
    await this.sendCommand(characteristic, ESCPOSCommands.CENTER);
    await this.sendCommand(characteristic, ESCPOSCommands.BOLD_ON);
    await this.writeText(characteristic, "ARQUEO DEL DÍA\n\n");
    await this.sendCommand(characteristic, ESCPOSCommands.BOLD_OFF);
    await this.sendCommand(characteristic, ESCPOSCommands.LEFT);

    // Fecha y hora
    const now = new Date();
    await this.writeText(
      characteristic,
      `Fecha: ${now.toLocaleDateString()}\n`
    );
    await this.writeText(characteristic, `Hora: ${now.toLocaleTimeString()}\n`);
    await this.writeText(characteristic, "------------------------\n\n");

    // Detalles de ventas
    await this.writeText(characteristic, "RESUMEN DE VENTAS\n");
    await this.writeText(
      characteristic,
      `Total Efectivo: $${totals.cash.toFixed(2)}\n`
    );
    await this.writeText(
      characteristic,
      `Total Tarjeta: $${totals.card.toFixed(2)}\n`
    );
    await this.writeText(characteristic, "------------------------\n");
    await this.sendCommand(characteristic, ESCPOSCommands.BOLD_ON);
    await this.writeText(
      characteristic,
      `TOTAL GENERAL: $${total.toFixed(2)}\n\n`
    );
    await this.sendCommand(characteristic, ESCPOSCommands.BOLD_OFF);

    // Métricas adicionales
    await this.writeText(
      characteristic,
      `Cantidad de órdenes: ${orderCount}\n`
    );
    await this.writeText(
      characteristic,
      `Ticket promedio: $${avgTicket.toFixed(2)}\n\n`
    );

    // // Detalles por mesa
    // const salesByTable = _.groupBy(sales, "mesaId");
    // await this.writeText(characteristic, "DETALLE POR MESA\n");
    // await this.writeText(characteristic, "------------------------\n");

    // for (const [mesaId, tableSales] of Object.entries(salesByTable)) {
    //   const tableTotal = tableSales.reduce((acc, sale) => acc + sale.amount, 0);
    //   await this.writeText(
    //     characteristic,
    //     `${mesaId}: $${tableTotal.toFixed(2)}\n`
    //   );
    // }

    // Pie de página
    await this.writeText(characteristic, "\n------------------------\n");
    await this.writeText(characteristic, "Fin del reporte\n\n\n");

    // Cortar papel
    await this.sendCommand(characteristic, ESCPOSCommands.CUT);
  }

  async getService(server) {
    const serviceUUIDs = [
      "000018f0-0000-1000-8000-00805f9b34fb",
      "49535343-fe7d-4ae5-8fa9-9fafd205e455",
      "e7810a71-73ae-499d-8c15-faa9aef0c3f2",
    ];

    for (const uuid of serviceUUIDs) {
      try {
        return await server.getPrimaryService(uuid);
      } catch {
        continue;
      }
    }
    throw new Error("No se encontró un servicio compatible");
  }

  async getWriteCharacteristic(service) {
    const characteristics = await service.getCharacteristics();
    return characteristics.find(
      (char) => char.properties.write || char.properties.writeWithoutResponse
    );
  }

  setupPrinterHandler(device, server, characteristic) {
    const printerHandler = {
      device,
      server,
      characteristic,
      name: device.name || "Impresora sin nombre",
      config: {
        printKitchenOrders: false,
        printBarOrders: false,
        autoCustomerBills: false,
      },
    };

    this.printerHandlers.set(device.id, printerHandler);
    //  this.setupDisconnectionListener(device);
    this.savePrinterConfigurations();
  }

  setupDisconnectionListener(device) {
    device.addEventListener("gattserverdisconnected", () => {
      console.log("Dispositivo desconectado:", device.id);
      const handler = this.printerHandlers.get(device.id);
      if (handler) {
        handler.characteristic = null;
        handler.server = null;
        this.printerHandlers.set(device.id, handler);
      }
    });
  }

  savePrinterConfigurations() {
    try {
      const printers = Array.from(this.printerHandlers.entries()).map(
        ([id, handler]) => ({
          id,
          name: handler.name,
          config: handler.config,
        })
      );

      localStorage.setItem("connectedPrinters", JSON.stringify(printers));

      const configs = {};
      this.printerHandlers.forEach((handler, id) => {
        configs[id] = handler.config;
      });
      localStorage.setItem("printerConfigurations", JSON.stringify(configs));
    } catch (error) {
      console.error("Error saving printer configurations:", error);
    }
  }

  async sendCommand(characteristic, command) {
    if (!characteristic) throw new Error("Característica no disponible");
    await characteristic.writeValue(new Uint8Array(command));
  }

  // async writeText(characteristic, text) {
  //   if (!characteristic) throw new Error("Característica no disponible");
  //   await characteristic.writeValue(this.encoder.encode(text));
  // }
  async writeText(characteristic, text) {
    if (!characteristic) throw new Error("Característica no disponible");

    // Convertir el texto completo a bytes
    const data = this.encoder.encode(text);

    // Dividir en chunks de 512 bytes o menos
    const chunkSize = 512;
    for (let i = 0; i < data.length; i += chunkSize) {
      const chunk = data.slice(i, Math.min(i + chunkSize, data.length));
      await characteristic.writeValue(chunk);

      // Pequeña pausa entre chunks para evitar sobrecarga
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  }

  // async ensureConnection(printerId) {
  //   console.log("ensureConnection");
  //   const handler = this.printerHandlers.get(printerId);
  //   if (!handler) {
  //     throw new Error("Impresora no encontrada");
  //   }

  //   if (
  //     !handler.characteristic ||
  //     !handler.server ||
  //     !handler.device.gatt.connected
  //   ) {
  //     await this.reconnectPrinter(printerId);
  //   }

  //   return handler;
  // }
  async ensureConnection(printerId) {
    if (!printerId) {
      throw new Error("ID de impresora no proporcionado");
    }

    const handler = this.printerHandlers.get(printerId);
    if (!handler) {
      throw new Error(`Impresora con ID ${printerId} no encontrada`);
    }

    try {
      if (
        !handler.characteristic ||
        !handler.server ||
        !handler.device?.gatt.connected
      ) {
        await this.reconnectPrinter(printerId);
      }

      // Verificar nuevamente después de la reconexión
      const updatedHandler = this.printerHandlers.get(printerId);
      if (!updatedHandler || !updatedHandler.characteristic) {
        throw new Error(
          `No se pudo establecer la conexión con la impresora ${printerId}`
        );
      }

      return updatedHandler;
    } catch (error) {
      throw new Error(
        `Error de conexión con impresora ${printerId}: ${error.message}`
      );
    }
  }

  // async reconnectPrinter(printerId) {
  //   const handler = this.printerHandlers.get(printerId);
  //   if (!handler) throw new Error("Impresora no encontrada");

  //   try {
  //     if (!handler.device || !handler.device.gatt) {
  //       const devices = await navigator.bluetooth.getDevices();
  //       const device = devices.find((d) => d.id === printerId);
  //       if (!device)
  //         throw new Error("Necesita volver a emparejar la impresora");
  //       handler.device = device;
  //     }

  //     const server = await handler.device.gatt.connect();
  //     const service = await this.getService(server);
  //     const characteristic = await this.getWriteCharacteristic(service);

  //     if (!characteristic) {
  //       throw new Error("No se pudo obtener la característica de escritura");
  //     }

  //     handler.server = server;
  //     handler.characteristic = characteristic;
  //     this.savePrinterConfigurations();
  //     return true;
  //   } catch (error) {
  //     handler.server = null;
  //     handler.characteristic = null;
  //     throw error;
  //   }
  // }
  async reconnectPrinter(printerId) {
    let attempts = 0;
    while (attempts < this.MAX_RECONNECT_ATTEMPTS) {
      try {
        const devices = await navigator.bluetooth.getDevices();
        const device = devices.find((d) => d.id === printerId);

        if (!device) {
          throw new Error("Necesita volver a emparejar la impresora");
        }

        const server = await device.gatt.connect();
        const service = await this.getService(server);
        const characteristic = await this.getWriteCharacteristic(service);

        if (!characteristic) {
          throw new Error("No se pudo obtener la característica de escritura");
        }

        // Actualizar el handler con la nueva conexión
        const handler = this.printerHandlers.get(printerId);
        if (handler) {
          handler.device = device;
          handler.server = server;
          handler.characteristic = characteristic;
          this.printerHandlers.set(printerId, handler);
          return handler;
        }

        throw new Error(
          "Handler de impresora no encontrado después de la reconexión"
        );
      } catch (error) {
        attempts++;
        if (attempts === this.MAX_RECONNECT_ATTEMPTS) {
          throw error;
        }
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }
  }

  updatePrinterConfig(printerId, config) {
    const handler = this.printerHandlers.get(printerId);
    if (handler) {
      // Actualizamos la configuración manteniendo los valores existentes
      handler.config = {
        ...handler.config,
        ...config,
      };

      // Guardamos la configuración actualizada
      this.savePrinterConfigurations();

      // Actualizamos el estado en memoria
      const updatedHandler = {
        ...handler,
        config: handler.config,
      };
      this.printerHandlers.set(printerId, updatedHandler);

      return true;
    }
    return false;
  }

  getPrinterConfig(printerId) {
    const handler = this.printerHandlers.get(printerId);
    return handler ? handler.config : null;
  }

  getAllPrinters() {
    return Array.from(this.printerHandlers.entries()).map(([id, handler]) => ({
      id,
      name: handler.name,
      config: handler.config,
      isConnected: !!(handler.characteristic && handler.device.gatt.connected),
    }));
  }
}

export const multiPrinterService = new MultiPrinterService();
